import { Clock } from "lucide-react";
import type { FocusEvent, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { usePopoverPosition } from "../../../hooks/usePopoverPosition";
import { ICON_SIZE, type InputSize, PAD_X, SIZE_CLASSES } from "../Input";
import { TRIGGER_BUTTON_CLASSES, TYPED_END_PAD } from "./pickerSizes";

export interface TimePickerProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;

  minuteStep?: number;
  placeholder?: string;
  disabled?: boolean;
  size?: InputSize;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MERIDIEMS = ["AM", "PM"] as const;
const LABEL_FORMATTER = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

function getMinutes(step: number): number[] {
  return Array.from({ length: 60 / step }, (_, i) => i * step);
}

function parseValue(value: string | undefined) {
  if (!value) return { hour24: null as number | null, minute: null as number | null };
  const [hour24, minute] = value.split(":").map(Number);
  return { hour24, minute };
}

function to24Hour(hour12: number, meridiem: "AM" | "PM"): number {
  const base = hour12 % 12;
  return meridiem === "PM" ? base + 12 : base;
}

function to12Hour(hour24: number): { hour12: number; meridiem: "AM" | "PM" } {
  const meridiem = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour12, meridiem };
}

function formatValue(value: string): string {
  const [hour24, minute] = value.split(":").map(Number);
  return LABEL_FORMATTER.format(new Date(2000, 0, 1, hour24, minute));
}

function maskTimeInput(raw: string): string {
  const letterIndex = raw.search(/[A-Za-z]/);
  const digitPart = letterIndex === -1 ? raw : raw.slice(0, letterIndex);
  const letterPart = letterIndex === -1 ? "" : raw.slice(letterIndex);
  const digits = digitPart.replace(/\D/g, "").slice(0, 4);

  let hourDigits = "";
  let minuteDigits = "";
  if (digits.length > 0) {
    const hourLength = digits[0] === "0" || digits[0] === "1" ? 2 : 1;
    hourDigits = digits.slice(0, hourLength);
    minuteDigits = digits.slice(hourLength, hourLength + 2);
  }
  const maskedDigits = [hourDigits, minuteDigits].filter(Boolean).join(":");

  const letters = letterPart
    .replace(/[^A-Za-z]/g, "")
    .slice(0, 2)
    .toUpperCase();

  return letters ? `${maskedDigits} ${letters}` : maskedDigits;
}

function parseTimeText(text: string): string | null {
  const trimmed = text.trim();

  const twelveHour = trimmed.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (twelveHour) {
    const hour12 = Number(twelveHour[1]);
    const minute = Number(twelveHour[2]);
    const meridiem = twelveHour[3].toUpperCase() as "AM" | "PM";
    if (hour12 < 1 || hour12 > 12 || minute < 0 || minute > 59) return null;
    const hour24 = to24Hour(hour12, meridiem);
    return `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }

  const twentyFourHour = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourHour) {
    const hour24 = Number(twentyFourHour[1]);
    const minute = Number(twentyFourHour[2]);
    if (hour24 < 0 || hour24 > 23 || minute < 0 || minute > 59) return null;
    return `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }

  return null;
}

export function TimePicker({
  label,
  value,
  onChange,
  minuteStep = 5,
  placeholder = "HH:MM AM/PM",
  disabled = false,
  size = "lg",
}: Readonly<TimePickerProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState(() => (value ? formatValue(value) : ""));
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hourColumnRef = useRef<HTMLDivElement>(null);
  const minuteColumnRef = useRef<HTMLDivElement>(null);

  const pointerDownRef = useRef(false);
  const [focusMethod, setFocusMethod] = useState<"pointer" | "keyboard" | null>(null);
  const isKeyboardFocus = focusMethod === "keyboard";
  const isFocused = focusMethod !== null;

  const handleInputPointerDown = (_event: ReactPointerEvent<HTMLInputElement>) => {
    pointerDownRef.current = true;
  };

  const handleInputFocus = (_event: FocusEvent<HTMLInputElement>) => {
    setFocusMethod(pointerDownRef.current ? "pointer" : "keyboard");
    pointerDownRef.current = false;
  };

  const handleInputBlur = (_event: FocusEvent<HTMLInputElement>) => {
    setFocusMethod(null);
  };

  const minutes = getMinutes(minuteStep);
  const { hour24, minute } = parseValue(value);
  const { hour12, meridiem } = hour24 !== null ? to12Hour(hour24) : { hour12: null, meridiem: null };

  const { panelRef, style: panelStyle } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef: fieldRef,
  });

  useEffect(() => {
    setInputText(value ? formatValue(value) : "");
  }, [value]);

  const commitTypedText = () => {
    const parsed = parseTimeText(inputText);
    if (parsed) {
      onChange(parsed);
      setInputText(formatValue(parsed));
    } else {
      setInputText(value ? formatValue(value) : "");
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    hourColumnRef.current?.querySelector('[aria-checked="true"]')?.scrollIntoView({ block: "nearest" });
    minuteColumnRef.current?.querySelector('[aria-checked="true"]')?.scrollIntoView({ block: "nearest" });
  }, [isOpen]);

  const commit = (nextHour12: number | null, nextMinute: number | null, nextMeridiem: "AM" | "PM" | null) => {
    const resolvedHour12 = nextHour12 ?? hour12 ?? 12;
    const resolvedMinute = nextMinute ?? minute ?? 0;
    const resolvedMeridiem = nextMeridiem ?? meridiem ?? "AM";
    const nextHour24 = to24Hour(resolvedHour12, resolvedMeridiem);
    onChange(`${String(nextHour24).padStart(2, "0")}:${String(resolvedMinute).padStart(2, "0")}`);
  };

  return (
    <div ref={containerRef} className="relative isolate">
      {label && <div className="mb-1.5 text-sm font-medium text-content-secondary">{label}</div>}
      <div
        ref={fieldRef}
        className={[
          "flex w-full items-center gap-1 border bg-surface-elevated transition-colors",
          SIZE_CLASSES[size],
          PAD_X[size].start,
          TYPED_END_PAD[size],
          isOpen || isFocused ? "border-border-focus" : "border-border-input",
          isKeyboardFocus ? "input-focus-ring" : "",
          disabled ? "cursor-not-allowed opacity-50" : "",
        ].join(" ")}
      >
        <input
          type="text"
          maxLength={8}
          disabled={disabled}
          value={inputText}
          onChange={(e) => setInputText(maskTimeInput(e.target.value))}
          onPointerDown={handleInputPointerDown}
          onFocus={handleInputFocus}
          onBlur={(e) => {
            handleInputBlur(e);
            commitTypedText();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitTypedText();
            } else if (e.key === "Escape") {
              setInputText(value ? formatValue(value) : "");
            }
          }}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-sm leading-none pb-0.5 text-content-primary outline-none placeholder:text-content-tertiary"
        />
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label="Open time picker"
          onClick={() => setIsOpen((open) => !open)}
          className={`btn-focus-ring grid ${TRIGGER_BUTTON_CLASSES[size]} shrink-0 cursor-pointer place-items-center rounded-md text-content-tertiary hover:bg-surface-base`}
        >
          <Clock size={ICON_SIZE[size]} />
        </button>
      </div>
      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={label ?? "Time"}
          style={panelStyle}
          className="z-[110] flex w-48 divide-x divide-border-subtle rounded-lg border border-border-input bg-surface-elevated shadow-lg"
        >
          <div ref={hourColumnRef} role="radiogroup" aria-label="Hour" className="max-h-48 flex-1 overflow-y-auto p-1">
            {HOURS.map((h) => (
              <button
                key={h}
                type="button"
                role="radio"
                aria-checked={h === hour12}
                onClick={() => commit(h, null, null)}
                className={[
                  "w-full cursor-pointer rounded-md px-2 py-1.5 text-center text-sm transition-colors",
                  h === hour12
                    ? "bg-brand-primary-subtle text-brand-primary-on-subtle"
                    : "text-content-primary hover:bg-surface-base",
                ].join(" ")}
              >
                {h}
              </button>
            ))}
          </div>
          <div
            ref={minuteColumnRef}
            role="radiogroup"
            aria-label="Minute"
            className="max-h-48 flex-1 overflow-y-auto p-1"
          >
            {minutes.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={m === minute}
                onClick={() => commit(null, m, null)}
                className={[
                  "w-full cursor-pointer rounded-md px-2 py-1.5 text-center text-sm transition-colors",
                  m === minute
                    ? "bg-brand-primary-subtle text-brand-primary-on-subtle"
                    : "text-content-primary hover:bg-surface-base",
                ].join(" ")}
              >
                {String(m).padStart(2, "0")}
              </button>
            ))}
          </div>
          <div role="radiogroup" aria-label="AM or PM" className="flex-1 p-1">
            {MERIDIEMS.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={m === meridiem}
                onClick={() => commit(null, null, m)}
                className={[
                  "w-full cursor-pointer rounded-md px-2 py-1.5 text-center text-sm transition-colors",
                  m === meridiem
                    ? "bg-brand-primary-subtle text-brand-primary-on-subtle"
                    : "text-content-primary hover:bg-surface-base",
                ].join(" ")}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
